import React, { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { UserRole } from '@/types';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Checkbox } from '@/components/ui/Checkbox';
import { Plus, Edit, Trash2, Eye, EyeOff, User as UserIcon, Building2, Layers, LayoutGrid } from 'lucide-react';
import { api } from '@/lib/api';
import { SYSTEM_MODULES } from '@/constants/modules';
import { toast } from 'react-hot-toast';
import { kanbanService, userKanbanService } from '@/services/kanbanService';
import { Kanban, UserKanban, User } from '@/types';

interface Company {
  id: string;
  name: string;
}


interface CreateUserData {
  name: string;
  email: string;
  password: string;
  role: UserRole;
  companyIds?: string[];
  gestorLeadsUserId?: number | null;
  allowedModules?: string[] | null;
}

interface UpdateUserData {
  name?: string;
  email?: string;
  password?: string;
  role?: UserRole;
  companyIds?: string[];
  gestorLeadsUserId?: number | null;
  allowedModules?: string[] | null;
}

export const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [kanbans, setKanbans] = useState<Kanban[]>([]);
  const [userKanbans, setUserKanbans] = useState<UserKanban[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState<CreateUserData>({
    name: '',
    email: '',
    password: '',
    role: UserRole.OPERADOR,
    companyIds: [],
    gestorLeadsUserId: null,
    allowedModules: null,
  });
  const [editFormData, setEditFormData] = useState<UpdateUserData>({
    name: '',
    email: '',
    password: '',
    role: UserRole.OPERADOR,
    companyIds: [],
    gestorLeadsUserId: null,
    allowedModules: null,
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const [usersResponse, companiesResponse, kanbansResponse, allUserKanbans] = await Promise.all([
        api.get('/users?limit=500'),
        api.get('/companies?limit=500&isActive=true'),
        kanbanService.getAllKanbans(),
        userKanbanService.getAllUserKanbans(),
      ]);
      setUsers(usersResponse.data.data.users);
      setCompanies(companiesResponse.data.data.companies);
      setKanbans(kanbansResponse);
      setUserKanbans(allUserKanbans);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadUserKanbans = async () => {
    try {
      const allUserKanbans = await userKanbanService.getAllUserKanbans();
      setUserKanbans(allUserKanbans);
    } catch (error) {
      toast.error('Erro ao recarregar associações');
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const getUserKanbans = (userId: string): UserKanban[] => {
    return userKanbans.filter(uk => uk.userId === userId);
  };

  const handleCreateUser = async () => {
    try {
      await api.post('/users', {
        ...formData,
        allowedModules: formData.allowedModules && formData.allowedModules.length > 0 ? formData.allowedModules : null,
      });
      toast.success('Usuário criado com sucesso');
      setShowCreateModal(false);
      setFormData({ name: '', email: '', password: '', role: UserRole.OPERADOR, companyIds: [], gestorLeadsUserId: null, allowedModules: null });
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao criar usuário');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      const updateData: UpdateUserData = {};
      if (editFormData.name) updateData.name = editFormData.name;
      if (editFormData.email) updateData.email = editFormData.email;
      if (editFormData.password) updateData.password = editFormData.password;
      if (editFormData.role) updateData.role = editFormData.role;
      if (editFormData.companyIds) updateData.companyIds = editFormData.companyIds;
      if (editFormData.gestorLeadsUserId !== undefined) updateData.gestorLeadsUserId = editFormData.gestorLeadsUserId;
      if (editFormData.allowedModules !== undefined) updateData.allowedModules = editFormData.allowedModules && editFormData.allowedModules.length > 0 ? editFormData.allowedModules : null;

      await api.put(`/users/${selectedUser.id}`, updateData);
      toast.success('Usuário atualizado com sucesso');
      setShowEditModal(false);
      setSelectedUser(null);
      setEditFormData({ name: '', email: '', password: '', role: UserRole.OPERADOR, companyIds: [], gestorLeadsUserId: null, allowedModules: null });
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar usuário');
    }
  };

  const handleToggleUserKanban = async (kanbanId: string) => {
    if (!selectedUser) return;

    try {
      const existingUserKanban = getUserKanbans(selectedUser.id).find(uk => uk.kanbanId === kanbanId);
      
      if (existingUserKanban) {
        // Remover associação
        await userKanbanService.deleteUserKanban(existingUserKanban.id);
        toast.success('Usuário removido do kanban');
        // Recarregar dados
        loadUserKanbans();
      } else {
        // Criar associação
        try {
          await userKanbanService.createUserKanban({
            userId: selectedUser.id,
            kanbanId: kanbanId,
          });
          toast.success('Usuário associado ao kanban');
          // Recarregar dados
          loadUserKanbans();
        } catch (error: any) {
          if (error.response?.status === 409) {
            // Usuário já está associado - criar associação local imediatamente
            console.log('🔄 Usuário já associado, criando associação local...');
            
            const newUserKanban: UserKanban = {
              id: `temp-${Date.now()}`,
              userId: selectedUser.id,
              kanbanId: kanbanId,
              etapasIds: null,
              createdAt: new Date(),
              updatedAt: new Date(),
              user: selectedUser,
              kanban: kanbans.find(k => k.id === kanbanId)!
            };
            
            // Atualizar estado imediatamente
            setUserKanbans(prev => {
              const exists = prev.some(uk => uk.userId === selectedUser.id && uk.kanbanId === kanbanId);
              if (!exists) {
                console.log('✅ Adicionando associação local:', newUserKanban);
                return [...prev, newUserKanban];
              }
              return prev;
            });
            
            toast.success('Usuário já estava associado ao kanban');
            
            // Recarregar dados reais após um tempo
            setTimeout(() => {
              console.log('🔄 Recarregando dados reais...');
              loadUserKanbans();
            }, 1000);
          } else {
            throw error;
          }
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao alterar associação');
    }
  };

  const handleInactivateUserKanban = async (kanbanId: string) => {
    if (!selectedUser) return;

    try {
      const existingUserKanban = getUserKanbans(selectedUser.id).find(uk => uk.kanbanId === kanbanId);
      
      if (existingUserKanban) {
        // Inativar associação removendo todas as etapas específicas
        await userKanbanService.updateUserKanban(existingUserKanban.id, {
          etapasIds: undefined, // Remove todas as etapas específicas
        });
        toast.success('Acesso ao kanban inativado (sem etapas específicas)');
        // Recarregar dados
        loadUserKanbans();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao inativar acesso');
    }
  };

  const handleToggleEtapa = async (kanbanId: string, etapaId: string, isChecked: boolean) => {
    if (!selectedUser) return;

    try {
      const userKanban = getUserKanbans(selectedUser.id).find(uk => uk.kanbanId === kanbanId);
      if (!userKanban) return;

      const currentEtapasIds = userKanban.etapasIds ? JSON.parse(userKanban.etapasIds) : [];
      let newEtapasIds: string[];

      if (isChecked) {
        // Adicionar etapa
        newEtapasIds = [...currentEtapasIds, etapaId];
      } else {
        // Remover etapa
        newEtapasIds = currentEtapasIds.filter((id: string) => id !== etapaId);
      }

      // Atualizar associação com novas etapas
      await userKanbanService.updateUserKanban(userKanban.id, {
        etapasIds: newEtapasIds.length > 0 ? newEtapasIds : undefined,
      });

      toast.success(`Etapa ${isChecked ? 'adicionada' : 'removida'} com sucesso`);
      
      // Recarregar dados
      loadUserKanbans();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao alterar etapa');
    }
  };

  const handleToggleStatus = async (userId: string) => {
    try {
      await api.patch(`/users/${userId}/toggle-status`);
      toast.success('Status do usuário alterado');
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao alterar status');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Tem certeza que deseja excluir este usuário?')) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success('Usuário excluído com sucesso');
      loadUsers();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao excluir usuário');
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    const rawModules = (user as any).allowedModules;
    const allowedModules = Array.isArray(rawModules) ? rawModules : (typeof rawModules === 'string' ? JSON.parse(rawModules) : null);
    setEditFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      companyIds: (user as any).userCompanies?.map((uc: any) => uc.company.id) || [],
      gestorLeadsUserId: (user as any).gestorLeadsUserId || null,
      allowedModules: allowedModules ?? null,
    });
    setShowEditModal(true);
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN: return 'Administrador';
      case UserRole.GESTOR: return 'Gestor';
      case UserRole.OPERADOR: return 'Operador';
      case UserRole.OFICINA: return 'Oficina';
      default: return role;
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMIN: return 'bg-red-100 text-red-800';
      case UserRole.GESTOR: return 'bg-blue-100 text-blue-800';
      case UserRole.OPERADOR: return 'bg-green-100 text-green-800';
      case UserRole.OFICINA: return 'bg-amber-100 text-amber-800';
      default: return 'bg-muted text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando usuários...</div>
      </div>
    );
  }

  return (
          <div className="space-y-6" data-testid="page-usuarios">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Gerenciamento de Usuários</h1>
          <p className="text-muted-foreground">Gerencie os usuários do sistema</p>
        </div>
        {(currentUser?.role === 'ADMIN' || currentUser?.role === 'GESTOR') && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Usuário
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Usuários</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Função</TableHead>
                <TableHead>Empresas</TableHead>
                <TableHead>Kanbans</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge className={getRoleColor(user.role)}>
                      {getRoleLabel(user.role)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(user as any).userCompanies && (user as any).userCompanies.length > 0 ? (
                        (user as any).userCompanies.map((uc: any) => (
                          <Badge key={uc.company.id} className="bg-blue-100 text-blue-800 text-xs">
                            {uc.company.name}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">Nenhuma empresa</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {getUserKanbans(user.id).length > 0 ? (
                        getUserKanbans(user.id).map((uk) => (
                          <Badge key={uk.id} className="bg-purple-100 text-purple-800 text-xs">
                            {uk.kanban?.nome}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-sm">Nenhum kanban</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {user.isActive ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(user.createdAt).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      {(currentUser?.role === 'ADMIN' || currentUser?.role === 'GESTOR') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(user)}
                          disabled={user.id === currentUser?.id || (currentUser?.role === 'GESTOR' && user.role !== 'OPERADOR')}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      )}
                      {(currentUser?.role === 'ADMIN' || currentUser?.role === 'GESTOR') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleStatus(user.id)}
                          disabled={user.id === currentUser?.id || (currentUser?.role === 'GESTOR' && user.role !== 'OPERADOR')}
                        >
                          {user.isActive ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      )}
                      {(currentUser?.role === 'ADMIN' || currentUser?.role === 'GESTOR') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={user.id === currentUser?.id || (currentUser?.role === 'GESTOR' && user.role !== 'OPERADOR')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Criar Novo Usuário"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Nome completo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@exemplo.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Senha</label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Senha"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Função</label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData({ ...formData, role: value as UserRole })}
              disabled={currentUser?.role === 'GESTOR'}
            >
              <option value={UserRole.OPERADOR}>Operador</option>
              {currentUser?.role === 'ADMIN' && (
                <option value={UserRole.OFICINA}>Oficina</option>
              )}
              {currentUser?.role === 'ADMIN' && (
                <>
                  <option value={UserRole.GESTOR}>Gestor</option>
                  <option value={UserRole.ADMIN}>Administrador</option>
                </>
              )}
            </Select>
            {currentUser?.role === 'GESTOR' && (
              <p className="text-xs text-muted-foreground mt-1">
                Gestores só podem criar usuários do tipo Operador
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Empresas</label>
            <div className="space-y-2 max-h-32 overflow-y-auto border rounded-md p-2">
              {companies.map((company) => (
                <label key={company.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.companyIds?.includes(company.id) || false}
                    onChange={(e) => {
                      const newCompanyIds = e.target.checked
                        ? [...(formData.companyIds || []), company.id]
                        : (formData.companyIds || []).filter(id => id !== company.id);
                      setFormData({ ...formData, companyIds: newCompanyIds });
                    }}
                    className="rounded"
                  />
                  <span className="text-sm">{company.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              ID do Usuário no GestorLeads
            </label>
            <Input
              type="number"
              value={formData.gestorLeadsUserId || ''}
              onChange={(e) => setFormData({ 
                ...formData, 
                gestorLeadsUserId: e.target.value ? parseInt(e.target.value, 10) : null 
              })}
              placeholder="Ex: 123 (opcional)"
            />
            <p className="text-xs text-muted-foreground mt-1">
              ID do usuário no GestorLeads para atribuição automática de tickets ao enviar mensagens
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Módulos de acesso</label>
            <p className="text-xs text-muted-foreground mb-2">
              Selecione os módulos que o usuário poderá acessar. Se nenhum for selecionado, o acesso segue pela função.
            </p>
            <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
              {SYSTEM_MODULES.map((mod) => {
                const checked = (formData.allowedModules || []).includes(mod.href);
                return (
                  <label key={mod.href} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = formData.allowedModules || [];
                        const next = e.target.checked
                          ? [...current, mod.href]
                          : current.filter((h) => h !== mod.href);
                        setFormData({ ...formData, allowedModules: next.length > 0 ? next : null });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{mod.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateUser}>
              Criar Usuário
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setActiveTab('personal');
        }}
        title="Editar Usuário"
        size="xl"
      >
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="personal" className="flex items-center space-x-2">
              <UserIcon className="h-4 w-4" />
              <span>Dados Pessoais</span>
            </TabsTrigger>
            <TabsTrigger value="companies" className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span>Empresas</span>
            </TabsTrigger>
            <TabsTrigger value="kanbans" className="flex items-center space-x-2">
              <Layers className="h-4 w-4" />
              <span>Kanbans</span>
            </TabsTrigger>
            <TabsTrigger value="modules" className="flex items-center space-x-2">
              <LayoutGrid className="h-4 w-4" />
              <span>Módulos</span>
            </TabsTrigger>
          </TabsList>

          {/* Aba Dados Pessoais */}
          <TabsContent value="personal" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Nome</label>
            <Input
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              placeholder="Nome completo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email</label>
            <Input
              type="email"
              value={editFormData.email}
              onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              placeholder="email@exemplo.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Senha (deixe em branco para não alterar)</label>
            <Input
              type="password"
              value={editFormData.password}
              onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
              placeholder="Nova senha (opcional)"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Função</label>
            <Select
              value={editFormData.role || ''}
              onValueChange={(value: string) => setEditFormData({ ...editFormData, role: value as UserRole })}
              disabled={currentUser?.role === 'GESTOR'}
            >
              <option value={UserRole.OPERADOR}>Operador</option>
              {currentUser?.role === 'ADMIN' && (
                <option value={UserRole.OFICINA}>Oficina</option>
              )}
              {currentUser?.role === 'ADMIN' && (
                <>
                  <option value={UserRole.GESTOR}>Gestor</option>
                  <option value={UserRole.ADMIN}>Administrador</option>
                </>
              )}
            </Select>
            {currentUser?.role === 'GESTOR' && (
              <p className="text-xs text-muted-foreground mt-1">
                Gestores só podem editar usuários do tipo Operador
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              ID do Usuário no GestorLeads
            </label>
            <Input
              type="number"
              value={editFormData.gestorLeadsUserId || ''}
              onChange={(e) => setEditFormData({ 
                ...editFormData, 
                gestorLeadsUserId: e.target.value ? parseInt(e.target.value, 10) : null 
              })}
              placeholder="Ex: 123 (opcional)"
            />
            <p className="text-xs text-muted-foreground mt-1">
              ID do usuário no GestorLeads para atribuição automática de tickets ao enviar mensagens
            </p>
          </div>
          </TabsContent>

          {/* Aba Empresas */}
          <TabsContent value="companies" className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Empresas</label>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                {companies.map((company) => (
                  <label key={company.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={editFormData.companyIds?.includes(company.id) || false}
                      onChange={(e) => {
                        const newCompanyIds = e.target.checked
                          ? [...(editFormData.companyIds || []), company.id]
                          : (editFormData.companyIds || []).filter(id => id !== company.id);
                        setEditFormData({ ...editFormData, companyIds: newCompanyIds });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{company.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Aba Kanbans */}
          <TabsContent value="kanbans" className="space-y-6">
            <div className="space-y-6">
              {kanbans.map((kanban) => {
                const userKanban = getUserKanbans(selectedUser?.id || '').find(uk => uk.kanbanId === kanban.id);
                const isAssociated = !!userKanban;
                
                return (
                  <div key={kanban.id} className="border rounded-lg p-4 space-y-4">
                    {/* Seção Kanban */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Kanban *
                      </label>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div
                            className="w-4 h-4 rounded-full flex-shrink-0"
                            style={{ backgroundColor: kanban.cor }}
                          />
                          <span className="text-sm font-medium">
                            {kanban.nome}
                          </span>
                          <Badge variant={kanban.isAtivo ? "default" : "secondary"} className="text-xs">
                            {kanban.isAtivo ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                        <div className="flex space-x-2">
                          {isAssociated && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleInactivateUserKanban(kanban.id)}
                              className="text-orange-600 border-orange-600 hover:bg-orange-50"
                            >
                              Inativar Acesso
                            </Button>
                          )}
                          <Button
                            variant={isAssociated ? "destructive" : "default"}
                            size="sm"
                            onClick={() => handleToggleUserKanban(kanban.id)}
                          >
                            {isAssociated ? 'Remover' : 'Associar'}
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Seção Etapas - Sempre mostrar */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground">
                        Etapas do Kanban
                      </label>
                      <div className="border rounded-md p-3 bg-background">
                        {kanban.etapas && Array.isArray(kanban.etapas) && kanban.etapas.length > 0 ? (
                          <div className="space-y-2">
                            {kanban.etapas.map((etapa) => {
                              const etapasIds = userKanban?.etapasIds ? JSON.parse(userKanban.etapasIds) : [];
                              const isEtapaSelected = isAssociated && etapasIds.includes(etapa.id);
                              
                              return (
                                <div key={etapa.id} className="flex items-center space-x-2">
                                  <Checkbox
                                    id={`etapa-${etapa.id}`}
                                    checked={isEtapaSelected}
                                    disabled={!isAssociated}
                                    onChange={() => 
                                      handleToggleEtapa(kanban.id, etapa.id, !isEtapaSelected)
                                    }
                                  />
                                  <div className="flex items-center space-x-2">
                                    <div
                                      className="w-3 h-3 rounded-full"
                                      style={{ backgroundColor: etapa.cor }}
                                    />
                                    <span className="text-sm">{etapa.nome}</span>
                                    <Badge variant="outline" className="text-xs">
                                      Ordem: {etapa.ordem}
                                    </Badge>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            Este kanban não possui etapas configuradas.
                          </p>
                        )}
                      </div>
                      {isAssociated && (
                        <p className="text-xs text-muted-foreground">
                          Deixe vazio para permitir acesso a todas as etapas
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {kanbans.length === 0 && (
                <p className="text-muted-foreground">Nenhum kanban disponível.</p>
              )}
            </div>
          </TabsContent>

          {/* Aba Módulos */}
          <TabsContent value="modules" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Selecione os módulos que este usuário poderá acessar. Se nenhum for selecionado, o acesso segue pela função (role).
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-3">
              {SYSTEM_MODULES.map((mod) => {
                const checked = (editFormData.allowedModules || []).includes(mod.href);
                return (
                  <label key={mod.href} className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 rounded p-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => {
                        const current = editFormData.allowedModules || [];
                        const next = e.target.checked
                          ? [...current, mod.href]
                          : current.filter((h) => h !== mod.href);
                        setEditFormData({ ...editFormData, allowedModules: next.length > 0 ? next : null });
                      }}
                      className="rounded"
                    />
                    <span className="text-sm">{mod.name}</span>
                    <span className="text-xs text-muted-foreground">{mod.href}</span>
                  </label>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Botões de ação */}
        <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => setShowEditModal(false)}>
            Cancelar
          </Button>
          <Button onClick={handleUpdateUser}>
            Atualizar Usuário
          </Button>
        </div>
      </Modal>
    </div>
  );
};
